import { useContext, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { Breadcrumb, Stack } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleCheck, faPrint, faUser } from '@fortawesome/free-solid-svg-icons';

import IconButton from '@components/Button/IconButton';
import { LinkButton } from '@components/Button';
import { RequisitionReceiptContent } from '@components/Requisition/RequisitionReceiptContent/RequisitionReceiptContent';
import {
  RequisitionNotifications,
  type RequisitionNotificationsAvailability,
} from '@components/Requisition/RequisitionNotifications';
import { ORDERS_AND_REPORTS, PATIENTS } from '@constants/routes';
import { useSettings } from '@hooks/useSettings';
import { Context } from '@store/store';
import { getReceiptPdfFilename, printReceiptPdf } from '@utils/downloadReceiptPdf';

import './RequisitionComplete.scss';

const RequisitionComplete = () => {
  const navigate = useNavigate();
  const [state, dispatch] = useContext(Context);
  const { isNatera } = useSettings();

  const contentRef = useRef<HTMLDivElement | null>(null);
  const [notificationsAvailability, setNotificationsAvailability] = useState<RequisitionNotificationsAvailability>(
    isNatera ? 'hidden' : 'loading',
  );

  const moveToRequisitions = () => {
    navigate(ORDERS_AND_REPORTS);
  };

  const printConfirmation = async () => {
    const element = contentRef.current;
    if (!element) return;

    await printReceiptPdf(element, getReceiptPdfFilename(state.newRequisition));
  };

  useEffect(() => {
    return () => {
      dispatch({ type: 'SET_NEW_REQUISITION', payload: null });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!state.newRequisition) {
    return null;
  }

  const patientChartPath = `${PATIENTS}/${state.newRequisition.individual_id}`;
  const showNotificationsColumn = !isNatera && notificationsAvailability !== 'hidden';

  return (
    <Stack
      gap={4}
      className='layout-single-col-py requisition-complete-container'
      data-testid='requisitionCompletePage'
    >
      <Breadcrumb listProps={{ className: 'mb-0' }}>
        <Breadcrumb.Item onClick={moveToRequisitions}>Orders and Reports</Breadcrumb.Item>
        <Breadcrumb.Item active>Order Complete</Breadcrumb.Item>
      </Breadcrumb>
      <section className='d-flex flex-column bg-white px-md-4 pb-5 pt-md-2 box-shadow-50 rounded-1'>
        <div className='requisition-complete-intro'>
          <FontAwesomeIcon icon={faCircleCheck} fontSize={40} className='d-print-none' />
          <div className='requisition-complete-heading'>
            <h1 className='mb-0 fw-semibold fs-3'>Order Confirmation</h1>
          </div>
        </div>
        <div className='p-4 pb-0 d-flex flex-column align-items-center gap-4'>
          <div
            className={[
              'requisition-complete-body',
              showNotificationsColumn ? '' : 'requisition-complete-body--receipt-only',
            ]
              .filter(Boolean)
              .join(' ')}
            data-testid='requisitionCompleteBody'
          >
            <RequisitionReceiptContent ref={contentRef} requisition={state.newRequisition} />
            {showNotificationsColumn ? (
              <div className='requisition-complete-notifications d-print-none'>
                <RequisitionNotifications
                  requisitionId={state.newRequisition.requisition_id}
                  onAvailabilityChange={setNotificationsAvailability}
                />
              </div>
            ) : null}
            <Stack
              direction='horizontal'
              gap={3}
              className='flex-column flex-md-row align-items-stretch justify-content-start requisition-complete-actions d-print-none'
            >
              <IconButton variant='outline-primary' onClick={moveToRequisitions}>
                Orders and Reports
              </IconButton>
              <IconButton variant='outline-primary' startIcon={faPrint} onClick={printConfirmation}>
                Print / Download
              </IconButton>
              {state.newRequisition.individual_id ? (
                <LinkButton
                  to={patientChartPath}
                  variant='outline-primary'
                  className='d-flex align-items-center'
                  data-testid='viewPatientChartLink'
                >
                  <FontAwesomeIcon icon={faUser} fontSize={16} className='me-2' aria-hidden />
                  View patient chart
                </LinkButton>
              ) : null}
            </Stack>
          </div>
        </div>
      </section>
    </Stack>
  );
};

export default RequisitionComplete;
